import { supabase } from './supabaseClient';
import {
  DEFAULT_APP_SETTINGS,
  mergeAppSettings,
  normalizeAppSettings,
  serializeAppSettings,
} from './appSettingsSchema';

const hashPin = async (pin) => {
  const salt = 'sensorpush_cloud_salt_2024';
  const combined = pin + salt;
  let hash = 0;
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
};

/**
 * Load native AppSettings from user_profiles (alert_settings JSONB + alert_phone).
 * Falls back to device_alert_rules.sms_phone_number when alert_phone is empty
 * (native SMS phone is often only on alert rules / local SQLite).
 */
export const getAppSettings = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data: profile, error } = await supabase
    .from('user_profiles')
    .select('id, email, full_name, alert_settings, alert_phone, is_pin_set')
    .eq('id', user.id)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') throw error;

  let alertPhone = profile?.alert_phone || '';
  if (!alertPhone) {
    const { data: rules } = await supabase
      .from('device_alert_rules')
      .select('sms_phone_number')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .not('sms_phone_number', 'is', null)
      .limit(1);
    alertPhone = rules?.[0]?.sms_phone_number || '';
  }

  const appSettings = normalizeAppSettings(profile?.alert_settings, alertPhone);

  return {
    userId: user.id,
    email: profile?.email || user.email || '',
    full_name: profile?.full_name || user.user_metadata?.full_name || '',
    is_pin_set: profile?.is_pin_set ?? false,
    appSettings,
  };
};

/** @deprecated Use getAppSettings — kept for any legacy callers. */
export const getUserSettings = async () => {
  const { email, full_name, is_pin_set, appSettings: s } = await getAppSettings();
  return {
    email,
    full_name,
    username: s.username || full_name || email?.split('@')[0] || '',
    is_pin_set,
    temperature_unit: s.temperatureUnit,
    distance_unit: s.distanceUnit,
    zone_trend_calculation: s.zoneTrendCalculation,
    push_notifications: s.pushNotificationsEnabled,
    email_alerts: s.emailAlerts,
    sms_alerts: s.alertsEnabled,
    status_alerts: s.statusAlerts,
    alert_phone: s.alertPhone,
    temp_high_trigger: s.tempAlertMax,
    temp_low_trigger: s.tempAlertMin,
    humidity_high_trigger: s.humidityAlertMax,
    humidity_low_trigger: s.humidityAlertMin,
    streaming_frequency: String(s.updateFrequency),
    data_sync_frequency: String(Math.round(s.syncInterval / 60)),
    ...s,
  };
};

/**
 * Persist native AppSettings patch to user_profiles.alert_settings (+ alert_phone column).
 */
export const updateAppSettings = async (patch = {}) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data: existing, error: fetchError } = await supabase
    .from('user_profiles')
    .select('alert_settings, email, full_name, alert_phone')
    .eq('id', user.id)
    .maybeSingle();

  if (fetchError) throw fetchError;

  const current = normalizeAppSettings(existing?.alert_settings, existing?.alert_phone || '');
  const next = mergeAppSettings(current, patch);

  const updateData = {
    updated_at: new Date().toISOString(),
    alert_settings: serializeAppSettings(next),
    alert_phone: next.alertPhone || null,
  };

  if (patch.email !== undefined) updateData.email = patch.email;
  if (patch.full_name !== undefined) updateData.full_name = patch.full_name;
  if (patch.username !== undefined) {
    updateData.alert_settings.username = patch.username;
    if (patch.full_name === undefined && !existing?.full_name) {
      updateData.full_name = patch.username;
    }
  }
  if (patch.alertPhone !== undefined) {
    updateData.alert_phone = patch.alertPhone || null;
    updateData.alert_settings.alertPhone = patch.alertPhone || '';
  }

  if (patch.pin_code !== undefined) {
    if (patch.pin_code && patch.pin_code.trim().length > 0) {
      updateData.pin_hash = await hashPin(patch.pin_code);
      updateData.is_pin_set = true;
      updateData.pin_created_at = new Date().toISOString();
      updateData.pin_updated_at = new Date().toISOString();
    } else {
      updateData.pin_hash = null;
      updateData.is_pin_set = false;
      updateData.pin_updated_at = new Date().toISOString();
    }
  }

  const { data, error } = await supabase
    .from('user_profiles')
    .upsert(
      {
        id: user.id,
        email: updateData.email || existing?.email || user.email,
        ...updateData,
      },
      { onConflict: 'id' }
    )
    .select('id, email, full_name, alert_settings, alert_phone, is_pin_set')
    .single();

  if (error) throw error;

  return {
    ...data,
    appSettings: normalizeAppSettings(data.alert_settings, data.alert_phone || ''),
  };
};

/** Accept legacy snake_case patches from older SettingsPage saves. */
export const updateUserSettings = async (legacyPatch) => {
  const patch = {};
  if (legacyPatch.email !== undefined) patch.email = legacyPatch.email;
  if (legacyPatch.full_name !== undefined) patch.full_name = legacyPatch.full_name;
  if (legacyPatch.username !== undefined) patch.username = legacyPatch.username;
  if (legacyPatch.alert_phone !== undefined) patch.alertPhone = legacyPatch.alert_phone;
  if (legacyPatch.pin_code !== undefined) patch.pin_code = legacyPatch.pin_code;
  if (legacyPatch.temperature_unit !== undefined) patch.temperatureUnit = legacyPatch.temperature_unit;
  if (legacyPatch.distance_unit !== undefined) patch.distanceUnit = legacyPatch.distance_unit;
  if (legacyPatch.zone_trend_calculation !== undefined) {
    patch.zoneTrendCalculation = legacyPatch.zone_trend_calculation;
  }
  if (legacyPatch.push_notifications !== undefined) {
    patch.pushNotificationsEnabled = legacyPatch.push_notifications;
  }
  if (legacyPatch.email_alerts !== undefined) patch.emailAlerts = legacyPatch.email_alerts;
  if (legacyPatch.sms_alerts !== undefined) patch.alertsEnabled = legacyPatch.sms_alerts;
  if (legacyPatch.status_alerts !== undefined) patch.statusAlerts = legacyPatch.status_alerts;
  if (legacyPatch.temp_low_trigger !== undefined) patch.tempAlertMin = legacyPatch.temp_low_trigger;
  if (legacyPatch.temp_high_trigger !== undefined) patch.tempAlertMax = legacyPatch.temp_high_trigger;
  if (legacyPatch.humidity_low_trigger !== undefined) {
    patch.humidityAlertMin = legacyPatch.humidity_low_trigger;
  }
  if (legacyPatch.humidity_high_trigger !== undefined) {
    patch.humidityAlertMax = legacyPatch.humidity_high_trigger;
  }
  if (legacyPatch.streaming_frequency !== undefined) {
    patch.updateFrequency = Number(legacyPatch.streaming_frequency);
  }
  if (legacyPatch.data_sync_frequency !== undefined) {
    patch.syncInterval = Number(legacyPatch.data_sync_frequency) * 60;
  }
  return updateAppSettings(patch);
};

export const updatePassword = async (newPassword) => {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
  return true;
};

export { DEFAULT_APP_SETTINGS };
